import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { FormattedMessage } from 'react-intl';

import {
  ConfirmationModal,
  LoadingPane,
} from '@folio/stripes/components';
import { stripesConnect } from '@folio/stripes/core';

import JobView from '../../components/job/job-view';
import {
  EntityNotFound,
} from '../components';
import CancelJobConfirmationModal from './components';

import {
  DETAIL_VIEW_PANE_WIDTH,
  JOB_FIELDS,
  getJobListUrl,
  getJobEditUrl,
} from '../../constants';
import { useCallout } from '../../hooks';
import handleActionJob from './utils';
import {
  JOB_ACTIONS,
} from '../../constants/job';
import {
  loadUsersForList,
} from '../../utils';

const JobViewRoute = ({
  history,
  location,
  mutator,
  match: { params: { id } },
}) => {
  const [job, setJob] = useState({});
  const [isJobLoaded, setIsJobLoaded] = useState(false);
  const [isJobFailed, setIsJobFailed] = useState(false);

  const [isJobActionSuccess, setIsJobActionSuccess] = useState(false);
  const [isConfirmStartJobModalOpen, setConfirmStartJobModalOpen] = useState(false);
  const [isConfirmCancelJobModalOpen, setConfirmCancelJobModalOpen] = useState(false);
  const [isConfirmRestartJobModalOpen, setConfirmRestartJobModalOpen] = useState(false);

  const showCallout = useCallout();

  const jobActionsCaller = handleActionJob({
    history,
    location,
    showCallout,
    setIsJobActionSuccess,
  });

  useEffect(
    () => {
      let isSubscribed = true;

      setIsJobLoaded(false);
      setIsJobFailed(false);
      setIsJobActionSuccess(false);
      mutator.viewJob.GET()
        .then((jobResponse) => {
          const jobTasks = [...jobResponse[JOB_FIELDS.ASSIGN_TASK]];

          return loadUsersForList(mutator, jobTasks)
            .then((updatedTasks) => {
              if (isSubscribed) {
                setJob({ ...jobResponse, ...updatedTasks });
              }
            });
        })
        .catch(() => {
          if (isSubscribed) {
            setIsJobFailed(true);
          }
        })
        .then(() => {
          if (isSubscribed) {
            setIsJobLoaded(true);
          }
        });

      return () => {
        isSubscribed = false;
      };
    },
    [id, isJobActionSuccess]
  );

  const onBack = useCallback(() => {
    history.push({
      pathname: getJobListUrl(),
      search: location.search,
      state: location.state,
    });
  }, [location.search, location.state, history]);

  const navigateToEdit = useCallback(() => {
    history.push(getJobEditUrl(id, location.search));
  }, [location.search, history, id]);

  const handleConfirmStartJobModalOpen = () => setConfirmStartJobModalOpen(!isConfirmStartJobModalOpen);

  const handleStartJob = () => {
    const requestBody = { id };
    const startAction = {
      requestBody,
      setModalOpen: setConfirmStartJobModalOpen,
      apiMethod: mutator.start.POST,
      calloutMessage: JOB_ACTIONS.STARTED
    };

    jobActionsCaller(startAction);
  };

  const handleConfirmCancelJobModalOpen = () => setConfirmCancelJobModalOpen(!isConfirmCancelJobModalOpen);

  const handleCancelJob = (comment) => {
    const requestBody = {
      id,
      comment,
      name: job[JOB_FIELDS.NAME],
    };
    const cancelAction = {
      requestBody,
      setModalOpen: setConfirmCancelJobModalOpen,
      apiMethod: mutator.cancel.POST,
      calloutMessage: JOB_ACTIONS.CANCELLED
    };

    jobActionsCaller(cancelAction);
  };

  const handleConfirmRestartJobModalOpen = () => setConfirmRestartJobModalOpen(!isConfirmRestartJobModalOpen);

  const handleRestartJob = () => {
    const requestBody = { id };
    const restartAction = {
      requestBody,
      setModalOpen: setConfirmRestartJobModalOpen,
      apiMethod: mutator.restart.POST,
      calloutMessage: JOB_ACTIONS.RESTARTED
    };

    jobActionsCaller(restartAction);
  };

  if (isJobFailed) {
    return (
      <EntityNotFound
        errorTextTranslationKey="ui-task-list.error.notFound.job"
        pageTitleTranslationKey="ui-task-list.title.view.job"
        onBack={onBack}
      />
    );
  }

  if (!isJobLoaded) {
    return (
      <LoadingPane
        dismissible
        defaultWidth={DETAIL_VIEW_PANE_WIDTH}
        paneTitle={<FormattedMessage id="ui-task-list.title.view.job" />}
        onClose={onBack}
      />
    );
  }

  return (
    <>
      <JobView
        job={job}
        onBack={onBack}
        onCancelJob={handleConfirmCancelJobModalOpen}
        onEdit={navigateToEdit}
        onRestartJob={handleConfirmRestartJobModalOpen}
        onStartJob={handleConfirmStartJobModalOpen}
      />
      <ConfirmationModal
        cancelLabel={<FormattedMessage id="ui-task-list.confirmationStartJobModal.cancel" />}
        confirmLabel={<FormattedMessage id="ui-task-list.confirmationStartJobModal.confirm" />}
        heading={<FormattedMessage id="ui-task-list.confirmationStartJobModal.heading.job" />}
        id="confirm-start-job-action"
        message={<FormattedMessage id="ui-task-list.confirmationStartJobModal.message.job" />}
        open={isConfirmStartJobModalOpen}
        onCancel={handleConfirmStartJobModalOpen}
        onConfirm={handleStartJob}
      />
      <ConfirmationModal
        cancelLabel={<FormattedMessage id="ui-task-list.confirmationRestartJobModal.cancel" />}
        confirmLabel={<FormattedMessage id="ui-task-list.confirmationRestartJobModal.confirm" />}
        heading={<FormattedMessage id="ui-task-list.confirmationRestartJobModal.heading.job" />}
        id="confirm-restart-job-action"
        message={<FormattedMessage id="ui-task-list.confirmationRestartJobModal.message.job" />}
        open={isConfirmRestartJobModalOpen}
        onCancel={handleConfirmRestartJobModalOpen}
        onConfirm={handleRestartJob}
      />
      <CancelJobConfirmationModal
        isOpen={isConfirmCancelJobModalOpen}
        onCancel={handleConfirmCancelJobModalOpen}
        onConfirm={handleCancelJob}
      />
    </>
  );
};

JobViewRoute.manifest = Object.freeze({
  viewJob: {
    type: 'okapi',
    path: 'tasks/jobs/:{id}',
    clientGeneratePk: false,
    throwErrors: false,
    accumulate: 'true',
    fetch: false,
  },
  start: {
    type: 'okapi',
    path: 'tasks/jobs/:{id}',
    clientGeneratePk: false,
    throwErrors: false,
    accumulate: 'true',
    fetch: false,
    POST: { path: 'tasks/jobs/:{id}/start' }
  },
  cancel: {
    type: 'okapi',
    path: 'tasks/jobs/:{id}',
    clientGeneratePk: false,
    throwErrors: false,
    accumulate: 'true',
    fetch: false,
    POST: { path: 'tasks/jobs/:{id}/cancel' }
  },
  restart: {
    type: 'okapi',
    path: 'tasks/jobs/:{id}',
    clientGeneratePk: false,
    throwErrors: false,
    accumulate: 'true',
    fetch: false,
    POST: { path: 'tasks/jobs/:{id}/restart' },
  },
  users: {
    type: 'okapi',
    path: 'users',
    clientGeneratePk: false,
    throwErrors: false,
    accumulate: true,
    fetch: false,
  },
});

JobViewRoute.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
  mutator: PropTypes.shape({
    cancel: PropTypes.shape({
      POST: PropTypes.func.isRequired,
    }),
    restart: PropTypes.shape({
      POST: PropTypes.func.isRequired,
    }),
    start: PropTypes.shape({
      POST: PropTypes.func.isRequired,
    }),
    users: PropTypes.shape({
      GET: PropTypes.func.isRequired,
    }),
    viewJob: PropTypes.shape({
      GET: PropTypes.func.isRequired,
    }),
  }),
};

export default stripesConnect(JobViewRoute);
